/**
 * Global keyboard shortcuts for the assembly viewer.
 *
 * Shortcuts:
 * - Delete / Backspace → delete selected module
 * - Ctrl+D / Cmd+D → duplicate selected module
 * - Ctrl+S / Cmd+S → save config
 * - Ctrl+Z / Cmd+Z → undo
 * - Ctrl+Shift+Z / Cmd+Shift+Z → redo
 * - Escape → deselect
 */

import { useEffect } from "react";
import { useViewerStore } from "../store";

interface ShortcutHandlers {
  onDelete: () => void;
  onDuplicate: () => void;
  onSave: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onDeselect: () => void;
}

export function useKeyboardShortcuts(handlers: ShortcutHandlers) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore when typing in input fields
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT" ||
        target.isContentEditable
      ) {
        return;
      }

      const isMod = e.metaKey || e.ctrlKey;
      const key = e.key.toLowerCase();

      // Delete / Backspace → delete selected module
      if (e.key === "Delete" || e.key === "Backspace") {
        const selected = useViewerStore.getState().selectedModuleInstanceId;
        if (selected) {
          e.preventDefault();
          handlers.onDelete();
        }
        return;
      }

      // Ctrl+D → duplicate
      if (isMod && key === "d") {
        e.preventDefault();
        handlers.onDuplicate();
        return;
      }

      // Ctrl+S → save
      if (isMod && key === "s") {
        e.preventDefault();
        handlers.onSave();
        return;
      }

      // Ctrl+Shift+Z → redo (check before Ctrl+Z)
      if (isMod && e.shiftKey && key === "z") {
        e.preventDefault();
        handlers.onRedo();
        return;
      }

      // Ctrl+Z → undo
      if (isMod && !e.shiftKey && key === "z") {
        e.preventDefault();
        handlers.onUndo();
        return;
      }

      // Escape → deselect
      if (e.key === "Escape") {
        handlers.onDeselect();
        return;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handlers]);
}
